import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPackage,
    FiAlertTriangle,
    FiTrendingUp,
    FiTrendingDown,
    FiRefreshCw,
    FiArrowRight,
    FiPlusCircle,
    FiList,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";

const TX_CONFIG = {
    "Stock In": { color: "#16a34a", bg: "#dcfce7" },
    "Stock Out": { color: "#dc2626", bg: "#fee2e2" },
    Return: { color: "#003b67", bg: "#e8f1f8" },
    Adjustment: { color: "#d97706", bg: "#fef3c7" },
};

function StatCard({ label, value, icon: Icon, color, sub, onClick }) {
    return (
        <div
            className="card"
            style={{
                padding: "1.1rem 1.25rem",
                borderLeft: `4px solid ${color}`,
                cursor: onClick ? "pointer" : "default",
            }}
            onClick={onClick}
        >
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    background: `${color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 8,
                }}
            >
                <Icon size={14} color={color} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>
                {label}
            </div>
            {sub && <div style={{ fontSize: 11.5, marginTop: 3, color }}>{sub}</div>}
        </div>
    );
}

export default function InventoryDashboard() {
    const navigate = useNavigate();
    const { academicYear } = useApp();
    const [summary, setSummary] = useState(null);
    const [lowStock, setLowStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const params = academicYear?._id ? { academicYear: academicYear._id } : {};
            const [sumRes, lowRes] = await Promise.all([
                inventoryService.getTransactionSummary(params),
                inventoryService.getLowStockItems(params),
            ]);
            setSummary(sumRes.data);
            setLowStock(lowRes.data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [academicYear?._id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchData();
    };

    const getTypeCount = (type) =>
        summary?.typeSummary?.find((t) => t._id === type)?.count || 0;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Inventory</h1>
                    <p className="page-subtitle">Stock management — College level</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiRefreshCw
                            size={13}
                            style={{
                                animation: refreshing ? "spin 0.7s linear infinite" : "none",
                            }}
                        />
                        Refresh
                    </button>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/inventory/items")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiList size={13} /> Items
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/inventory/transactions/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiPlusCircle size={13} /> New Transaction
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="loading-center">
                    <div className="spinner" />
                    Loading inventory…
                </div>
            ) : (
                <>
                    {/* ── Stat Cards ── */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                            gap: "1rem",
                            marginBottom: "1.25rem",
                        }}
                    >
                        <StatCard
                            label="Total Items"
                            value={summary?.totalItems || 0}
                            icon={FiPackage}
                            color="#003b67"
                            onClick={() => navigate("/inventory/items")}
                        />
                        <StatCard
                            label="Low Stock"
                            value={summary?.lowStockCount || 0}
                            icon={FiAlertTriangle}
                            color="#dc2626"
                            sub={summary?.lowStockCount > 0 ? "Needs attention" : "All good"}
                            onClick={() => navigate("/inventory/items?lowStock=true")}
                        />
                        <StatCard
                            label="Stock In (30d)"
                            value={getTypeCount("Stock In")}
                            icon={FiTrendingUp}
                            color="#16a34a"
                        />
                        <StatCard
                            label="Stock Out (30d)"
                            value={getTypeCount("Stock Out")}
                            icon={FiTrendingDown}
                            color="#d97706"
                        />
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 320px",
                            gap: "1rem",
                            alignItems: "start",
                        }}
                    >
                        {/* ── Recent Transactions ── */}
                        <div className="card">
                            <div
                                className="card-header"
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <span>Recent Transactions</span>
                                <button
                                    className="btn btn-outline btn-sm"
                                    onClick={() => navigate("/inventory/transactions")}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.35rem",
                                    }}
                                >
                                    View All <FiArrowRight size={12} />
                                </button>
                            </div>
                            {!summary?.recentTransactions?.length ? (
                                <div
                                    className="card-body"
                                    style={{
                                        textAlign: "center",
                                        color: "var(--text-muted)",
                                        fontSize: 13,
                                    }}
                                >
                                    No transactions yet.
                                </div>
                            ) : (
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th style={{ textAlign: "center" }}>Type</th>
                                                <th style={{ textAlign: "center" }}>Qty</th>
                                                <th style={{ textAlign: "center" }}>Stock After</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.recentTransactions.map((tx) => {
                                                const tc =
                                                    TX_CONFIG[tx.type] || TX_CONFIG["Adjustment"];
                                                return (
                                                    <tr key={tx._id}>
                                                        <td>
                                                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                                {tx.item?.name}
                                                            </div>
                                                            {tx.issuedToName && (
                                                                <div
                                                                    style={{
                                                                        fontSize: 11.5,
                                                                        color: "var(--text-muted)",
                                                                    }}
                                                                >
                                                                    {tx.issuedToName}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ textAlign: "center" }}>
                                                            <span
                                                                style={{
                                                                    ...tc,
                                                                    padding: "2px 8px",
                                                                    borderRadius: 99,
                                                                    fontSize: 11.5,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {tx.type}
                                                            </span>
                                                        </td>
                                                        <td
                                                            style={{
                                                                textAlign: "center",
                                                                fontWeight: 700,
                                                                color: tc.color,
                                                            }}
                                                        >
                                                            {tx.type === "Stock Out"
                                                                ? `-${tx.quantity}`
                                                                : `+${tx.quantity}`}{" "}
                                                            {tx.item?.unit}
                                                        </td>
                                                        <td style={{ textAlign: "center", fontSize: 13 }}>
                                                            {tx.stockAfter} {tx.item?.unit}
                                                        </td>
                                                        <td
                                                            style={{
                                                                fontSize: 12,
                                                                color: "var(--text-muted)",
                                                            }}
                                                        >
                                                            {tx.transactionDate}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* ── Low Stock Alerts ── */}
                        <div className="card">
                            <div
                                className="card-header"
                                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                            >
                                <FiAlertTriangle size={13} color="#dc2626" />
                                Low Stock Alerts
                                {lowStock.length > 0 && (
                                    <span
                                        style={{
                                            fontSize: 11,
                                            background: "#dc2626",
                                            color: "#fff",
                                            padding: "1px 7px",
                                            borderRadius: 99,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {lowStock.length}
                                    </span>
                                )}
                            </div>
                            {lowStock.length === 0 ? (
                                <div
                                    className="card-body"
                                    style={{
                                        fontSize: 13,
                                        color: "#16a34a",
                                        textAlign: "center",
                                    }}
                                >
                                    ✓ All items are adequately stocked.
                                </div>
                            ) : (
                                <div>
                                    {lowStock.map((item) => (
                                        <div
                                            key={item._id}
                                            onClick={() => navigate(`/inventory/items/${item._id}`)}
                                            style={{
                                                padding: "0.65rem 1rem",
                                                borderBottom: "1px solid var(--border)",
                                                cursor: "pointer",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "#fff5f5";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "transparent";
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                    {item.name}
                                                </div>
                                                <div
                                                    style={{ fontSize: 11.5, color: "var(--text-muted)" }}
                                                >
                                                    {item.category?.name} · Min: {item.minStockLevel}{" "}
                                                    {item.unit}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: "right" }}>
                                                <div
                                                    style={{
                                                        fontSize: 15,
                                                        fontWeight: 800,
                                                        color:
                                                            item.currentStock === 0 ? "#dc2626" : "#d97706",
                                                    }}
                                                >
                                                    {item.currentStock}
                                                </div>
                                                <div
                                                    style={{ fontSize: 10.5, color: "var(--text-muted)" }}
                                                >
                                                    {item.unit}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div style={{ padding: "0.6rem 1rem" }}>
                                        <button
                                            className="btn btn-outline btn-sm"
                                            style={{ width: "100%", justifyContent: "center" }}
                                            onClick={() => navigate("/inventory/items?lowStock=true")}
                                        >
                                            View All Low Stock Items
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Quick Actions ── */}
                    <div className="card" style={{ marginTop: "1rem" }}>
                        <div className="card-header">Quick Actions</div>
                        <div
                            className="card-body"
                            style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}
                        >
                            {[
                                {
                                    label: "Stock In",
                                    to: "/inventory/transactions/new?type=Stock In",
                                    color: "#16a34a",
                                },
                                {
                                    label: "Stock Out",
                                    to: "/inventory/transactions/new?type=Stock Out",
                                    color: "#dc2626",
                                },
                                {
                                    label: "Return",
                                    to: "/inventory/transactions/new?type=Return",
                                    color: "#003b67",
                                },
                                {
                                    label: "Adjustment",
                                    to: "/inventory/transactions/new?type=Adjustment",
                                    color: "#d97706",
                                },
                                {
                                    label: "Add Item",
                                    to: "/inventory/items/new",
                                    color: "#7c3aed",
                                },
                                {
                                    label: "Suppliers",
                                    to: "/inventory/suppliers",
                                    color: "#0891b2",
                                },
                                {
                                    label: "Categories",
                                    to: "/inventory/categories",
                                    color: "#6b7280",
                                },
                            ].map(({ label, to, color }) => (
                                <button
                                    key={label}
                                    className="btn btn-outline btn-sm"
                                    style={{ borderColor: color, color }}
                                    onClick={() => navigate(to)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
